<template>
  <CompanyManAppLayout
    page-title="Field Tickets"
    page-subtitle="Approve or reject wellsite tickets"
  >
    <div class="space-y-6 pt-4">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold text-white">Field Ticket Approvals</h1>
          <p class="text-slate-400 text-sm mt-1">Tickets submitted by the service company for your sign-off.</p>
        </div>
        <div class="flex gap-2">
          <button class="btn" :class="filter===''?'active':''" @click="filter=''; load()">All</button>
          <button class="btn" :class="filter==='submitted'?'active':''" @click="filter='submitted'; load()">Pending</button>
          <button class="btn" :class="filter==='approved'?'active':''" @click="filter='approved'; load()">Approved</button>
        </div>
      </div>

      <p v-if="error" class="text-red-400">{{ error }}</p>
      <p v-if="message" class="text-emerald-400">{{ message }}</p>
      <div v-if="loading" class="text-slate-400 py-10 text-center">Loading…</div>

      <div v-else class="panel">
        <div v-if="!tickets.length" class="text-slate-400 py-10 text-center">No tickets in this view.</div>
        <table v-else class="w-full text-sm">
          <thead class="text-slate-400 border-b border-slate-700">
            <tr>
              <th class="py-2 text-left">Ticket</th>
              <th class="py-2 text-left">Well / Site</th>
              <th class="py-2 text-left">Date</th>
              <th class="py-2 text-left">Total</th>
              <th class="py-2 text-left">Status</th>
              <th class="py-2 text-left"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tickets" :key="t.id" class="border-b border-slate-800 text-slate-200">
              <td class="py-3">
                <div class="font-medium text-white">{{ t.number }}</div>
                <div class="text-xs text-slate-500">{{ t.serviceType || t.title }}</div>
              </td>
              <td class="py-3">{{ t.wellName || t.siteName || t.customerName || '—' }}</td>
              <td class="py-3">{{ t.workDate || '—' }}</td>
              <td class="py-3 text-emerald-400">${{ Number(t.totalAmount || 0).toLocaleString() }}</td>
              <td class="py-3 capitalize">{{ String(t.status).replace('_',' ') }}</td>
              <td class="py-3">
                <button class="link" @click="openTicket(t.id)">{{ t.status === 'submitted' ? 'Review' : 'View' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="selected" class="modal" @click.self="selected=null">
        <div class="modal-panel">
          <div class="flex justify-between gap-3 mb-4">
            <div>
              <p class="text-xs text-slate-500 font-mono">{{ selected.number }}</p>
              <h2 class="text-xl font-bold text-white">{{ selected.serviceType || selected.title }}</h2>
              <p class="text-slate-400 text-sm">{{ selected.wellName || selected.siteName }} · {{ selected.customerName }}</p>
            </div>
            <p class="text-2xl font-bold text-emerald-400">${{ Number(selected.totalAmount || 0).toLocaleString() }}</p>
          </div>

          <table class="w-full text-sm mb-4">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 text-left">Item</th>
                <th class="py-2 text-right">Qty</th>
                <th class="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in selected.lines || []" :key="line.id" class="border-b border-slate-800">
                <td class="py-2 text-slate-200">
                  <div>{{ line.description }}</div>
                  <div class="text-xs text-slate-500">{{ line.employee || line.lineType }}</div>
                </td>
                <td class="py-2 text-right text-slate-300">{{ line.quantity }} {{ line.unit }}</td>
                <td class="py-2 text-right text-emerald-400">${{ Number(line.amount).toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>

          <p v-if="selected.fieldNotes" class="text-sm text-slate-300 mb-4 whitespace-pre-wrap">{{ selected.fieldNotes }}</p>

          <template v-if="selected.status === 'submitted'">
            <label class="block text-sm text-slate-400 mb-1">Your name</label>
            <input v-model="signer" class="input mb-3" />
            <label class="block text-sm text-slate-400 mb-1">Notes</label>
            <textarea v-model="notes" rows="2" class="input mb-4"></textarea>
            <div class="flex gap-3 justify-end">
              <button class="btn" @click="selected=null">Close</button>
              <button class="btn danger" :disabled="busy" @click="decide('reject')">Reject</button>
              <button class="btn primary" :disabled="busy || !signer.trim()" @click="decide('approve')">Approve</button>
            </div>
          </template>
          <div v-else class="flex justify-end">
            <button class="btn" @click="selected=null">Close</button>
          </div>
        </div>
      </div>
    </div>
  </CompanyManAppLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import CompanyManAppLayout from '@/components/layout/CompanyManAppLayout.vue'
import { buildApiUrl } from '@/config/api'

const loading = ref(true)
const busy = ref(false)
const error = ref('')
const message = ref('')
const filter = ref('submitted')
const tickets = ref<any[]>([])
const selected = ref<any | null>(null)
const signer = ref(localStorage.getItem('user_name') || 'Mike Operator')
const notes = ref('')

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`, 'Content-Type': 'application/json' }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const qs = filter.value ? `?status=${encodeURIComponent(filter.value)}` : ''
    const res = await fetch(buildApiUrl(`company-man/field-tickets${qs}`), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load')
    tickets.value = data.fieldTickets || []
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

async function openTicket(id: number) {
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`company-man/field-tickets/${id}`), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to open')
    selected.value = data.ticket
    notes.value = ''
  } catch (e: any) {
    error.value = e.message || 'Failed to open'
  }
}

async function decide(action: 'approve' | 'reject') {
  if (!selected.value) return
  busy.value = true
  error.value = ''
  message.value = ''
  try {
    const res = await fetch(buildApiUrl(`company-man/field-tickets/${selected.value.id}/${action}`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ name: signer.value, notes: notes.value, reason: notes.value }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Action failed')
    message.value = data.message || (action === 'approve' ? 'Approved' : 'Rejected')
    selected.value = null
    await load()
  } catch (e: any) {
    error.value = e.message || 'Action failed'
  } finally {
    busy.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.panel { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6; }
.btn { @apply px-3 py-2 rounded-lg bg-slate-700/60 border border-slate-600 text-slate-200 text-sm font-semibold; }
.btn.active, .btn.primary { @apply bg-emerald-500/90 border-emerald-400 text-slate-900; }
.btn.danger { @apply bg-red-500/80 border-red-400 text-white; }
.link { @apply text-emerald-400 font-semibold; }
.modal { @apply fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4; }
.modal-panel { @apply w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl p-6; }
.input { @apply w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-xl text-white; }
</style>
